import { FC } from "react";
import Stack from "@mui/material/Stack";
import { Skeleton, Typography } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";
import { money } from "@/utils/numeral/money";
import {
  ResourceFiltersWithAction,
  ResourceType,
} from "@/services/cloudchipr.api";
import { getCleanActionValue } from "@/utils/clean-options";
import { AutomationsFoundResourcesGridToolbarActionName } from "@/components/pages/automations/common/components/found-resources/components/toolbar/AutomationsFoundResourcesGridToolbarActionName";
import { AutomationsFoundResourcesGridToolbarFilters } from "@/components/pages/automations/common/components/found-resources/components/toolbar/AutomationsFoundResourcesGridToolbarFilters";
import { AgGridGlobalSearch } from "@/components/pages/common/ag-grid/components/global-search/AgGridGlobalSearch";
import { AGGridColumnsToolPanel } from "@/components/pages/common/ag-grid/components/AGGridColumnsToolPanel";
import { useAppSelector } from "@/store/hooks";
import { gridResourcesFilteredCountSelector } from "@/store/resources-grid/selectors/resource-data/counts/gridResourcesFilteredCountSelector";
import { gridResourcesEmsSelector } from "@/store/resources-grid/selectors/resource-data/costs/gridResourcesEmsSelector";
import { gridResourceDataLoadingSelector } from "@/store/resources-grid/selectors/resource-data/gridResourceDataLoadingSelector";

interface AutomationsFoundResourcesGridToolbarProps {
  resourceType: ResourceType;
  filters: ResourceFiltersWithAction;
  scheduleAction: string | null;
  gridApi: GridApi | null;
  masterGridId: string;
}

export const AutomationsFoundResourcesGridToolbar: FC<
  AutomationsFoundResourcesGridToolbarProps
> = ({ resourceType, filters, scheduleAction, masterGridId, gridApi }) => {
  const count = useAppSelector((state) =>
    gridResourcesFilteredCountSelector(state, masterGridId),
  );
  const ems = useAppSelector((state) =>
    gridResourcesEmsSelector(state, masterGridId),
  );
  const loading = useAppSelector((state) =>
    gridResourceDataLoadingSelector(state, masterGridId),
  );
  const emsText = ems ? `- ${money(ems)}` : "";

  const resourceName = getResourceTypeName(resourceType, {
    type: "long",
  });

  const { action, snapshot, filter } = filters;

  return (
    <Stack px={0.5} py={1.5} direction="row" justifyContent="space-between">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          variant="subtitle1"
          fontWeight="medium"
          sx={{ display: "flex", gap: 1 }}
        >
          {resourceName}

          {loading ? <Skeleton width={40} variant="text" /> : ` (${count}) `}
          {loading ? <Skeleton width={80} variant="text" /> : emsText}
        </Typography>

        <AutomationsFoundResourcesGridToolbarActionName
          resourceType={resourceType}
          scheduleAction={scheduleAction}
          action={getCleanActionValue(action, snapshot)}
        />

        <AutomationsFoundResourcesGridToolbarFilters filter={filter} />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <AgGridGlobalSearch gridApi={gridApi} />
        <AGGridColumnsToolPanel gridApi={gridApi} />
      </Stack>
    </Stack>
  );
};
